import React, { useState, useMemo } from 'react';

const STAWKI_VAT = ['23', '8', '5', '0', 'zw'];
const MIN_DLUGOSC_POWODU = 10;

/**
 * Panel dla uprawnionego (admin lub kierownik z akcja import_bez_cennika).
 * Renderowac TYLKO gdy moznaOdblokowac === true.
 *
 * WARIANT A (zalecany): uzupelnienie nazwy i VAT recznie -> produkty realnie
 * powstaja, pokrycie robi sie 100% i nic nie jest obchodzone.
 * WARIANT B (awaryjny): zapis faktury mimo brakow, z powodem w logu.
 *
 * Komponent NIE wola API sam - oddaje dane przez onZapiszProdukty(pozycje)
 * i onZapiszMimoBrakow(powod), zeby cala komunikacja zostala w kreatorze.
 */
const AdminFallbackPanel = ({ brakujace = [], onZapiszProdukty, onZapiszMimoBrakow, isSaving = false }) => {
  const [wiersze, setWiersze] = useState(() =>
    (brakujace || []).map(b => ({
      kod_produktu: b.kod_produktu || b.kod || '',
      ean: b.ean || '',
      nazwa: '',
      stawka_vat: '',
      jednostka: 'szt.'
    }))
  );
  const [powod, setPowod] = useState('');
  const [potwierdzone, setPotwierdzone] = useState(false);

  const zmien = (idx, pole, wartosc) => {
    setWiersze(prev => prev.map((w, i) => (i === idx ? { ...w, [pole]: wartosc } : w)));
  };

  // Wariant A wymaga KOMPLETU: kazdy wiersz z nazwa i jawnym VAT-em
  const wariantAGotowy = useMemo(
    () => wiersze.length > 0 && wiersze.every(w => (w.nazwa || '').trim().length > 0 && w.stawka_vat !== ''),
    [wiersze]
  );
  const wariantBGotowy = powod.trim().length >= MIN_DLUGOSC_POWODU && potwierdzone;

  const inputStyle = {
    width: '100%', padding: '4px 6px', fontSize: '12px',
    border: '1px solid var(--border)', borderRadius: '4px',
    backgroundColor: 'var(--card-bg)', color: 'var(--text)'
  };
  const thStyle = { padding: '0.4rem', textAlign: 'left', borderBottom: '1px solid var(--border)', fontSize: '11px' };
  // Przycisk wylaczony: #fff na var(--border) to w motywie jasnym kontrast 1.23:1
  // (napis znika). Oba przyciski startuja WYLACZONE, wiec to stan trwaly.
  const styleAkcji = (kolor, wylaczony) => ({
    padding: '6px 12px', fontSize: '11px', borderRadius: '4px', fontWeight: '600',
    border: `1px solid ${wylaczony ? 'var(--border)' : kolor}`,
    backgroundColor: wylaczony ? 'var(--bg)' : kolor,
    color: wylaczony ? 'var(--text)' : '#fff',
    cursor: wylaczony ? 'not-allowed' : 'pointer'
  });
  const tdStyle = { padding: '0.3rem 0.4rem', borderBottom: '1px solid var(--border)' };

  return (
    <div style={{ marginTop: '12px' }}>
      {/* WARIANT A */}
      <div style={{
        border: '2px solid var(--success)', borderRadius: '6px',
        backgroundColor: 'var(--card-bg)', padding: '12px'
      }}>
        <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--success)' }}>
          {'✅'} Zalecane: uzupe{'ł'}nij dane r{'ę'}cznie
        </div>
        <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px', marginBottom: '8px' }}>
          Produkty powstan{'ą'} z poprawn{'ą'} nazw{'ą'} i stawk{'ą'} VAT, a faktura zapisze si{'ę'} normaln{'ą'} {'ś'}cie{'ż'}k{'ą'}.
        </div>

        <div style={{ maxHeight: '260px', overflowY: 'auto', border: '1px solid var(--border)', borderRadius: '4px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: 'var(--bg)' }}>
                <th style={{ ...thStyle, width: '110px' }}>Kod</th>
                <th style={{ ...thStyle, width: '130px' }}>EAN</th>
                <th style={thStyle}>Nazwa <span style={{ color: 'var(--danger)' }}>*</span></th>
                <th style={{ ...thStyle, width: '90px' }}>VAT <span style={{ color: 'var(--danger)' }}>*</span></th>
                <th style={{ ...thStyle, width: '90px' }}>Jednostka</th>
              </tr>
            </thead>
            <tbody>
              {wiersze.map((w, idx) => (
                <tr key={idx}>
                  <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '11px' }}>{w.kod_produktu || '-'}</td>
                  <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '11px' }}>{w.ean || '-'}</td>
                  <td style={tdStyle}>
                    <input
                      type="text"
                      value={w.nazwa}
                      placeholder="Nazwa produktu"
                      onChange={e => zmien(idx, 'nazwa', e.target.value)}
                      style={{
                        ...inputStyle,
                        borderColor: (w.nazwa || '').trim() ? 'var(--border)' : 'var(--danger)'
                      }}
                    />
                  </td>
                  <td style={tdStyle}>
                    <select
                      value={w.stawka_vat}
                      onChange={e => zmien(idx, 'stawka_vat', e.target.value)}
                      style={{
                        ...inputStyle,
                        borderColor: w.stawka_vat !== '' ? 'var(--border)' : 'var(--danger)'
                      }}
                    >
                      <option value="">— wybierz —</option>
                      {STAWKI_VAT.map(s => (
                        <option key={s} value={s}>{s === 'zw' ? 'zw' : `${s}%`}</option>
                      ))}
                    </select>
                  </td>
                  <td style={tdStyle}>
                    <input
                      type="text"
                      value={w.jednostka}
                      onChange={e => zmien(idx, 'jednostka', e.target.value)}
                      style={inputStyle}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            onClick={() => onZapiszProdukty && onZapiszProdukty(wiersze)}
            disabled={!wariantAGotowy || isSaving}
            style={styleAkcji('var(--success)', !wariantAGotowy || isSaving)}
          >
            {isSaving ? '⏳ Zapisywanie...' : 'Uzupełnij dane i utwórz produkty'}
          </button>
          {!wariantAGotowy && (
            <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
              Ka{'ż'}dy wiersz musi mie{'ć'} nazw{'ę'} i wybran{'ą'} stawk{'ę'} VAT
            </span>
          )}
        </div>
      </div>

      {/* WARIANT B */}
      <div style={{
        border: '1px dashed var(--danger)', borderRadius: '6px',
        backgroundColor: 'var(--bg)', padding: '12px', marginTop: '10px'
      }}>
        <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--danger)' }}>
          {'⚠️'} Awaryjnie (gorsze rozwi{'ą'}zanie): zapisz mimo brak{'ó'}w
        </div>
        <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px', marginBottom: '8px' }}>
          Powód trafi do uwag faktury i do logu. U{'ż'}ywaj tylko, gdy dostawca naprawd{'ę'} nie ma cennika.
        </div>

        <textarea
          value={powod}
          onChange={e => setPowod(e.target.value)}
          placeholder="Powód pominięcia cennika (min. 10 znaków)"
          rows={2}
          style={{
            width: '100%', padding: '6px', fontSize: '12px', borderRadius: '4px',
            border: '1px solid var(--border)', backgroundColor: 'var(--card-bg)',
            color: 'var(--text)', resize: 'vertical'
          }}
        />
        <div style={{ fontSize: '10px', color: powod.trim().length >= MIN_DLUGOSC_POWODU ? 'var(--text-muted)' : 'var(--danger)' }}>
          {powod.trim().length} / {MIN_DLUGOSC_POWODU} znak{'ó'}w
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', marginTop: '6px', cursor: 'pointer' }}>
          <input type="checkbox" checked={potwierdzone} onChange={e => setPotwierdzone(e.target.checked)} />
          Rozumiem: te pozycje nie wejd{'ą'} na stan magazynowy ani do FIFO
        </label>

        <button
          onClick={() => onZapiszMimoBrakow && onZapiszMimoBrakow(powod.trim())}
          disabled={!wariantBGotowy || isSaving}
          style={{ marginTop: '8px', ...styleAkcji('var(--danger)', !wariantBGotowy || isSaving) }}
        >
          {isSaving ? '⏳ Zapisywanie...' : 'Zapisz mimo braków'}
        </button>
      </div>
    </div>
  );
};

export default AdminFallbackPanel;
